// Demo-mode chrome: the persistent read-only banner and the per-page guidance cards
// that tell a first-time visitor what to look at and why it matters. Both render null
// outside demo mode, so Layout can mount them unconditionally.

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { exitDemoMode, isDemoMode } from './demoMode';

export function DemoBanner() {
  if (!isDemoMode()) return null;

  const leave = (to: string) => {
    exitDemoMode();
    // Full reload so the auth/org/query caches rebuild from the real (or empty) session.
    window.location.href = to;
  };

  return (
    <div className="demo-banner" role="status">
      <span>
        <strong>Demo workspace</strong> — a fictional 5-developer team with sample data.
        Everything is explorable, nothing is editable.
      </span>
      <span className="demo-banner-actions">
        <button className="btn btn-primary" onClick={() => leave('/signup')}>
          Create your workspace
        </button>
        <button className="btn btn-ghost" onClick={() => leave('/login')}>
          Exit demo
        </button>
      </span>
    </div>
  );
}

interface Hint {
  key: string;
  title: string;
  body: string;
}

// Matched top-down; first hit wins. Copy is written for a lead evaluating the product:
// each hint names the concrete thing to look at and the decision it informs.
const HINTS: Array<{ match: (path: string) => boolean; hint: Hint }> = [
  {
    match: p => p.startsWith('/dashboard'),
    hint: {
      key: 'dashboard',
      title: 'Your team at a glance — start with the "By developer" table',
      body:
        'Jake Miller logged 19 h against a ~9 h AI estimate on his last task — that\'s the conversation this product surfaces. Olena delivers under estimate with heavy AI use, saving ~20 h vs the no-AI estimate on one story. Try switching the estimate mode in the top bar: every accuracy metric follows.',
    },
  },
  {
    match: p => /^\/analyses\/[^/]+/.test(p),
    hint: {
      key: 'analysis-detail',
      title: 'The raw evidence behind one pull request',
      body:
        'Every estimate ships with complexity, confidence, risk factors and a work breakdown — and is posted back to the PR as a GitHub comment, so the whole team sees the same numbers.',
    },
  },
  {
    match: p => p.startsWith('/analyses'),
    hint: {
      key: 'analyses',
      title: 'One row = one task, with its pull requests rolled up',
      body:
        'Estimates and logged hours are compared at task grain, never per-PR. Open NOVA-252 (a customer address book that took twice the estimate) to see why, or switch to the Pull requests tab — there\'s a failed analysis waiting for a retry.',
    },
  },
  {
    match: p => p.startsWith('/work-items'),
    hint: {
      key: 'work-item',
      title: 'Estimate vs reality for one task',
      body:
        'Aggregated AI-assisted and no-AI estimates against actual logged hours, plus a plain-English summary per pull request — enough to judge effort and scope without reading a line of code.',
    },
  },
  {
    match: p => p.startsWith('/developers'),
    hint: {
      key: 'developers',
      title: 'Estimates are calibrated per person',
      body:
        'Level and experience feed the model — a senior\'s four hours is not a junior\'s four hours. Jira account mapping is what links logged time to each developer automatically.',
    },
  },
  {
    match: p => p.startsWith('/projects'),
    hint: {
      key: 'projects',
      title: 'Control what gets analyzed (and billed)',
      body:
        'Branch policies decide which merged PRs consume credits. Here payments-api only analyzes main and release/* — feature-branch noise never costs a credit.',
    },
  },
  {
    match: p => p.startsWith('/billing'),
    hint: {
      key: 'billing',
      title: 'Pay per analyzed pull request — no subscription',
      body:
        '1 credit = 1 analyzed PR, and failed analyses are refunded automatically (see the history below). This team\'s month so far cost under $2.',
    },
  },
  {
    match: p => p.startsWith('/settings'),
    hint: {
      key: 'settings',
      title: 'Two integrations, about five minutes',
      body:
        'A read-only GitHub App supplies the pull requests; Jira supplies logged time. Teammates get invited here with owner/admin/member roles — in the demo these actions are disabled.',
    },
  },
];

const HINT_DISMISS_PREFIX = 'aima.demo.hint.';

export function DemoHint() {
  const location = useLocation();
  // Location is read before the early return, so the hook order never changes.
  const hint = HINTS.find(h => h.match(location.pathname))?.hint;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  if (!isDemoMode() || !hint) return null;
  if (dismissedKey === hint.key) return null;
  if (sessionStorage.getItem(HINT_DISMISS_PREFIX + hint.key) === '1') return null;

  const dismiss = () => {
    sessionStorage.setItem(HINT_DISMISS_PREFIX + hint.key, '1');
    setDismissedKey(hint.key);
  };

  return (
    <div className="demo-hint" role="note">
      <div className="demo-hint-body">
        <div className="demo-hint-title">💡 {hint.title}</div>
        <p>{hint.body}</p>
      </div>
      <button className="demo-hint-dismiss" onClick={dismiss} aria-label="Dismiss hint">
        ✕
      </button>
    </div>
  );
}
